/**
 * Player Performance Table
 * Shows top or worst performing players
 */
import { getRoiColor } from '@utils/colorUtils';
import type { ReactElement } from 'react';
import type { PlayerPerformance } from '../hooks/useLabDashboard';

// =============================================================================
// Types
// =============================================================================

interface PlayerPerformanceTableProps {
  title: string;
  data: PlayerPerformance[];
  variant: 'success' | 'danger';
}

function getHitRateColor(hitRate: number): string {
  const percent = hitRate * 100;
  if (percent >= 60) return 'text-theme-success';
  if (percent >= 55) return 'text-green-300';
  if (percent >= 50) return 'text-theme-text-secondary';
  if (percent >= 40) return 'text-theme-warning';
  return 'text-theme-danger';
}

// =============================================================================
// Component
// =============================================================================

export function PlayerPerformanceTable({
  title,
  data,
  variant,
}: PlayerPerformanceTableProps): ReactElement {
  if (data.length === 0) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-theme-text-secondary mb-3">{title}</h3>
        <p className="text-theme-text-tertiary text-sm">No data available</p>
      </div>
    );
  }

  const headerColor = variant === 'success' ? 'text-theme-success' : 'text-theme-danger';

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4 flex flex-col max-h-80">
      <h3 className={`text-sm font-medium ${headerColor} mb-3 shrink-0`}>{title}</h3>
      <div className="overflow-auto flex-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-theme-text-secondary text-left border-b border-theme-border">
              <th className="pb-2 font-medium">Player</th>
              <th className="pb-2 font-medium w-12">W</th>
              <th className="pb-2 font-medium w-12">L</th>
              <th className="pb-2 font-medium w-16">Hit %</th>
              <th className="pb-2 font-medium w-16">ROI</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx} className="border-b border-theme-border/50 last:border-0">
                <td className="py-2 text-theme-text font-medium">{row.playerName}</td>
                <td className="py-2 text-theme-text-secondary">{row.hits}</td>
                <td className="py-2 text-theme-text-secondary">{row.misses}</td>
                <td className={`py-2 ${getHitRateColor(row.hitRate)}`}>
                  {(row.hitRate * 100).toFixed(1)}%
                </td>
                <td className={`py-2 font-medium ${getRoiColor(row.roi)}`}>
                  {row.roi >= 0 ? '+' : ''}
                  {row.roi.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
